import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import {
  useNotifications,
  useUnreadNotificationsCount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`;
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
  if (seconds < 604800)
    return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

/**
 * Shared notification bell.
 * Defaults to product (admin/vendor) ink styling.
 * Pass `buttonClassName` for marketing header (white on black).
 */
const NotificationBell = ({ className = '', buttonClassName = '', onBroadcastClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: notificationsData, isLoading } = useNotifications(
    { per_page: 10 },
    { enabled: isOpen }
  );
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = notificationsData?.data?.data || [];

  const getNotificationsUrl = () => {
    if (user?.user_type === 'admin') return '/admin/notifications';
    if (user?.user_type === 'vendor') return '/vendor/notifications';
    return '/notifications';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead.mutateAsync(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead.mutateAsync();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_confirmed':
      case 'booking_completed':
        return '✅';
      case 'booking_cancelled':
        return '❌';
      case 'payment_received':
        return '💰';
      case 'event_started':
        return '▶️';
      case 'event_ended':
        return '⏹️';
      case 'external_lead':
      case 'consultation_lead':
        return '📋';
      case 'broadcast':
        return '📢';
      default:
        return '🔔';
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative rounded-full p-1.5 transition-colors sm:p-2 ${
          buttonClassName ||
          'text-ink-tertiary hover:bg-surface-muted hover:text-ink'
        }`}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-[10001] mt-2 flex max-h-[500px] w-80 flex-col overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)] sm:w-96">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-medium text-ink">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-1 text-xs font-medium text-ink-secondary transition-colors hover:text-ink"
                disabled={markAllAsRead.isPending}
              >
                <CheckCheck className="h-3 w-3" />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-sm text-ink-secondary">
                Loading notifications…
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`cursor-pointer border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-muted ${
                    !notification.is_read ? 'bg-surface-muted/80' : ''
                  }`}
                  onClick={() => {
                    if (!notification.is_read) {
                      handleMarkAsRead(notification.id);
                    }
                    if (notification.is_broadcast && onBroadcastClick) {
                      onBroadcastClick(notification);
                      return;
                    }
                    const href = notification.data?.href;
                    if (href) {
                      setIsOpen(false);
                      navigate(href);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0 text-lg">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-ink">
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-ink" />
                        )}
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-ink-secondary">
                        {notification.message}
                      </p>
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-xs text-ink-tertiary">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                        {notification.is_broadcast && (
                          <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs text-ink-secondary">
                            Broadcast
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-sm text-ink-secondary">
                No notifications
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="border-t border-border px-4 py-2.5">
              <Link
                to={getNotificationsUrl()}
                className="block text-center text-xs font-medium text-ink transition-opacity hover:opacity-70"
                onClick={() => setIsOpen(false)}
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
